// Post-invite waiting room (UC-5): after sending an invite the inviter lands
// here — a board in a "waiting for opponent" state with the time control shown
// and the shareable link handy. The challenge lives server-side, so leaving and
// coming back is safe. Advancing to the live game is driven by the challenge
// flipping to `accepted` (covers both the newcomer-registers and member-accepts
// cases); presence makes it feel instant for the member case (UC-6).

import { Chessground } from "chessground";
import { api, ApiError, type InviteInfo } from "../api.ts";
import { esc, h } from "../dom.ts";
import { navigate, type Params } from "../router.ts";
import { rememberReturn } from "../returnContext.ts";
import { currentUser } from "../session.ts";
import { onSessionMessage } from "../presence.ts";
import { focusOrNavigate } from "../ui/tabs.ts";
import { onDisconnect } from "../ui/lifecycle.ts";
import { t } from "../i18n/index.ts";
import { brandLockup, BRAND_NAME } from "../ui/brand.ts";

const START_FEN = "rnbqkbnr/pppppppp/8/8/8/8/PPPPPPPP/RNBQKBNR w KQkq - 0 1";
const POLL_MS = 3000;

export async function waitingScreen(params: Params): Promise<HTMLElement> {
  const token = params.token ?? "";
  const user = currentUser();
  if (!user) {
    rememberReturn(`/wait/${token}`);
    navigate("/login");
    return h(`<main class="card"><p class="msg">${t("common.redirecting")}</p></main>`);
  }

  let info: InviteInfo;
  try {
    info = await api.resolveInvite(token);
  } catch (err) {
    return notice(err instanceof ApiError ? err.message : t("waiting.brokenLink"));
  }

  // Already accepted → jump straight into the game.
  if (info.status === "accepted" && info.game_id) {
    navigate(`/game/${info.game_id}`);
    return h(`<main class="card"><p class="msg">${t("common.openingGame")}</p></main>`);
  }
  if (!info.usable) {
    return notice(
      info.expired ? t("waiting.expired") : t("waiting.noLongerOpen"),
    );
  }

  const opponent = info.email ?? t("waiting.yourFriend");
  const orientation = info.color === "black" ? "black" : "white";
  const inviteUrl = `${location.origin}/i/${token}`;

  const el = h(`
    <main class="game waiting">
      <div class="board-col">
        <div class="cg-board-wrap"></div>
        <div class="wait-badge">${t("waiting.forOpponent")}</div>
      </div>
      <aside class="side">
        <div class="side-top">
          <span class="conn ok">${t("waiting.waiting")}</span>
          <span class="tc">${esc(info.tc)}${info.rated ? ` · ${t("chess.rated")}` : ` · ${t("chess.casual")}`}</span>
        </div>

        <div class="panel">
          <h2>${t("waiting.on", { who: `<span class="opp">${esc(opponent)}</span>` })}</h2>
          <p class="tagline">${t("waiting.tagline")}</p>
          <button class="btn primary" id="nudge" type="button">${t("waiting.ready")}</button>
          <p class="fine">${t("invite.shareLink")}</p>
          <input class="copy" readonly value="${esc(inviteUrl)}" />
        </div>

        <div class="controls">
          <a class="btn ghost" data-link href="/">${t("waiting.leave")}</a>
        </div>
        <p class="note" role="status" aria-live="polite"></p>
      </aside>
    </main>
  `);

  const copy = el.querySelector<HTMLInputElement>(".copy")!;
  copy.addEventListener("focus", () => copy.select());

  // "I'm ready" → fire a Ready-signal to the invitee (UC-3). The challenge id is
  // the part of the token before the signature dot.
  const note = el.querySelector<HTMLElement>(".note")!;
  const nudge = el.querySelector<HTMLButtonElement>("#nudge")!;
  nudge.hidden = !info.can_notify;
  const challengeId = token.slice(0, token.indexOf("."));
  nudge.addEventListener("click", async () => {
    nudge.disabled = true;
    note.textContent = t("common.sending");
    try {
      const { delivery } = await api.sendReady(challengeId, "ready");
      note.textContent =
        delivery.state === "sent" || delivery.state === "acknowledged_in_app"
          ? t("waiting.nudged", { who: opponent })
          : delivery.state === "queued"
            ? t("waiting.willLet", { who: opponent })
            : t("waiting.nudgeFailed");
    } catch (err) {
      note.textContent = err instanceof ApiError ? err.message : t("waiting.nudgeFailed");
    } finally {
      window.setTimeout(() => (nudge.disabled = false), 2000);
    }
  });

  // View-only board in the start position, oriented to the inviter's colour.
  requestAnimationFrame(() => {
    const boardEl = el.querySelector<HTMLElement>(".cg-board-wrap")!;
    const cg = Chessground(boardEl, {
      fen: START_FEN,
      orientation,
      viewOnly: true,
      coordinates: false,
      drawable: { enabled: false },
    });

    let cleaned = false;
    const cleanup = () => {
      if (cleaned) return;
      cleaned = true;
      clearInterval(poll);
      unsub();
      cg.destroy();
    };

    const goToGame = (gameId: string) => {
      cleanup();
      // Focus an existing board tab if one is already open (plan item 5).
      void focusOrNavigate(gameId, () => navigate(`/game/${gameId}`));
    };

    // Re-resolve the challenge; advance the moment it flips to accepted.
    const check = async () => {
      try {
        const next = await api.resolveInvite(token);
        if (next.status === "accepted" && next.game_id) {
          goToGame(next.game_id);
        } else if (next.expired) {
          el.querySelector<HTMLElement>(".note")!.textContent = t("waiting.expiredPoll");
        }
      } catch {
        /* transient; keep polling */
      }
    };

    const poll = window.setInterval(check, POLL_MS);
    // The accepted Ready-signal for *this* challenge carries the game id, so jump
    // straight in rather than waiting for the next poll — closes the window where
    // the badge lingered after an email-accept (plan item 5). Any other session
    // event still triggers a re-check as a fallback.
    const unsub = onSessionMessage((msg) => {
      if (msg.t === "ready" && msg.d.game_id && msg.d.challenge_id === challengeId) {
        goToGame(msg.d.game_id);
        return;
      }
      if (
        msg.t === "notification" &&
        msg.d.context.game_id &&
        msg.d.context.challenge_id === challengeId
      ) {
        goToGame(msg.d.context.game_id);
        return;
      }
      void check();
    });

    onDisconnect(el, cleanup);
  });

  return el;
}

function notice(body: string): HTMLElement {
  return h(`
    <main class="card">
      <h1 class="brand" aria-label="${BRAND_NAME}">${brandLockup}</h1>
      <p class="msg bad">${esc(body)}</p>
      <p><a class="btn ghost" data-link href="/">${t("common.backHome")}</a></p>
    </main>
  `);
}
